import { App } from '@capacitor/app'
import { api, type FunnelEventName, type SignupAttributionPayload } from './api'
import { getAffiliateBrowserId } from './affiliateAttribution'
import { hasConsent } from './consent/storage'
import { getNativePlatform, isNativeMobile } from '../mobile/capacitor'

const FIRST_TOUCH_KEY = 'optioneer_signup_first_touch_v1'
const PENDING_SIGNUP_KEY = 'optioneer_pending_signup_attribution_v1'
const LANDING_EVENT_KEY = 'optioneer_landing_viewed_v1'

const UTM_KEYS = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term'] as const
const CLICK_KEYS = ['gclid', 'gbraid', 'wbraid'] as const
const PATH_LIMIT = 2048

type FirstTouch = {
  initial_landing_path: string
  referrer?: string
  utm_source?: string
  utm_medium?: string
  utm_campaign?: string
  utm_content?: string
  utm_term?: string
  anonymous_id?: string
}

type PendingSignup = SignupAttributionPayload & {
  signup_started_at?: string
  account_id?: string
  account_created_at?: string
}

type SignupAccount = { id: string; created_at: string; last_sign_in_at?: string }

// Keep the original allowlisted campaign context in memory while a visitor makes
// their consent choice. Storage receives only the categories currently allowed.
let originalTouch: FirstTouch | null = null
let flushInFlight: Promise<boolean> | null = null
let landingInFlight = false

function storageGet<T>(key: string): T | null {
  try {
    const value = window.localStorage.getItem(key)
    return value ? (JSON.parse(value) as T) : null
  } catch {
    return null
  }
}

function storageSet(key: string, value: unknown) {
  try {
    window.localStorage.setItem(key, JSON.stringify(value))
  } catch {
    // Attribution is best-effort and must never block navigation or signup.
  }
}

function storageRemove(key: string) {
  try {
    window.localStorage.removeItem(key)
  } catch {
    // Storage restrictions must not block authentication.
  }
}

/** Only public campaign parameters belong in acquisition paths, never auth state. */
function acquisitionPath(value: string, analytics: boolean, marketing: boolean): string {
  try {
    const url = new URL(value, window.location.origin)
    if (url.origin !== window.location.origin) return '/'
    const params = new URLSearchParams()
    // Keep whole click identifiers; omit an overlong value instead of corrupting it.
    for (const key of [...(marketing ? CLICK_KEYS : []), ...(analytics ? UTM_KEYS : [])]) {
      const raw = url.searchParams.get(key)?.trim()
      if (!raw || raw.length > (UTM_KEYS.includes(key as (typeof UTM_KEYS)[number]) ? 255 : 512))
        continue
      const candidate = new URLSearchParams(params)
      candidate.set(key, raw)
      if (`${url.pathname}?${candidate.toString()}`.length <= PATH_LIMIT) params.set(key, raw)
    }
    const query = params.toString()
    return `${url.pathname.slice(0, PATH_LIMIT)}${query ? `?${query}` : ''}`
  } catch {
    return '/'
  }
}

function isServiceReturn(): boolean {
  const params = new URLSearchParams(window.location.search)
  return (
    window.location.pathname === '/auth/callback' ||
    (window.location.pathname === '/pricing' && params.has('subscription')) ||
    (['/settings', '/admin/broker-sync'].includes(window.location.pathname) &&
      params.has('snaptrade'))
  )
}

function originalReferrer(): string | undefined {
  if (!document.referrer || isServiceReturn()) return undefined
  try {
    const url = new URL(document.referrer)
    if (!['https:', 'http:'].includes(url.protocol) || url.origin === window.location.origin)
      return undefined
    // Origin is sufficient to identify a referrer and cannot expose its private query/path.
    return url.origin
  } catch {
    return undefined
  }
}

function consentFilteredTouch(touch: FirstTouch): FirstTouch {
  const analytics = hasConsent('analytics')
  const marketing = hasConsent('marketing')
  const filtered: FirstTouch = {
    initial_landing_path: acquisitionPath(touch.initial_landing_path, analytics, marketing),
  }
  if (analytics) {
    const referrer = touch.referrer
    if (referrer) {
      try {
        const url = new URL(referrer)
        if (['http:', 'https:'].includes(url.protocol)) filtered.referrer = url.origin
      } catch {
        // Invalid old attribution is omitted.
      }
    }
    const params = new URL(touch.initial_landing_path, window.location.origin).searchParams
    for (const key of UTM_KEYS) {
      const value = (touch[key] || params.get(key))?.trim()
      if (value) filtered[key] = value.slice(0, 255)
    }
    filtered.anonymous_id = touch.anonymous_id || getAffiliateBrowserId()
  }
  return filtered
}

export function captureFirstTouch(): FirstTouch {
  const existing = storageGet<FirstTouch>(FIRST_TOUCH_KEY)
  // Storage can be cleared by the user or consent cleanup during the same SPA session.
  if (!existing && originalTouch && !storageGet<PendingSignup>(PENDING_SIGNUP_KEY))
    originalTouch = null
  if (!originalTouch) {
    originalTouch = existing || {
      initial_landing_path: acquisitionPath(window.location.href, true, true),
      referrer: originalReferrer(),
    }
    if (!existing) {
      // Existing UTM columns have their own limits. Keep every valid campaign
      // field even when a long click URL cannot fit all fields in the safe path.
      const params = new URLSearchParams(window.location.search)
      for (const key of UTM_KEYS) {
        const value = params.get(key)?.trim()
        if (value) originalTouch[key] = value.slice(0, 255)
      }
    }
  }
  const firstTouch = consentFilteredTouch(originalTouch)
  storageSet(FIRST_TOUCH_KEY, firstTouch)
  return firstTouch
}

function safePath(): string {
  // Signup routing metadata needs a path, not private query parameters.
  return window.location.pathname.slice(0, PATH_LIMIT)
}

function detectWebDevice(): Pick<
  SignupAttributionPayload,
  'device_type' | 'signup_surface' | 'operating_system' | 'browser'
> {
  const ua = navigator.userAgent.toLowerCase()
  const device_type =
    /ipad|tablet/.test(ua) || (/android/.test(ua) && !/mobile/.test(ua))
      ? 'tablet'
      : /iphone|ipod|android.*mobile|windows phone/.test(ua)
        ? 'mobile'
        : ua
          ? 'desktop'
          : 'unknown'
  const operating_system = /android/.test(ua)
    ? 'Android'
    : /iphone|ipad|ipod/.test(ua)
      ? 'iOS'
      : /windows/.test(ua)
        ? 'Windows'
        : /macintosh|mac os/.test(ua)
          ? 'macOS'
          : /linux/.test(ua)
            ? 'Linux'
            : 'Unknown'
  const browser = /edg\//.test(ua)
    ? 'Edge'
    : /opr\/|opera/.test(ua)
      ? 'Opera'
      : /firefox\/|fxios\//.test(ua)
        ? 'Firefox'
        : /chrome\/|crios\//.test(ua)
          ? 'Chrome'
          : /safari\//.test(ua)
            ? 'Safari'
            : 'Unknown'
  return { device_type, signup_surface: 'web', operating_system, browser }
}

export async function collectSignupAttribution(): Promise<SignupAttributionPayload> {
  const firstTouch = captureFirstTouch()
  const base = {
    ...firstTouch,
    signup_path: safePath(),
  }

  if (!isNativeMobile()) {
    return { ...base, ...detectWebDevice() }
  }

  const platform = getNativePlatform()
  let appInfo: { version?: string; build?: string } = {}
  try {
    appInfo = await App.getInfo()
  } catch {
    // Some preview/native test contexts do not expose App.getInfo.
  }

  return {
    ...base,
    device_type: 'mobile',
    signup_surface:
      platform === 'android' ? 'android_app' : platform === 'ios' ? 'ios_app' : 'unknown',
    operating_system: platform === 'android' ? 'Android' : platform === 'ios' ? 'iOS' : 'Unknown',
    app_version: appInfo.version,
    app_build: appInfo.build,
  }
}

export async function prepareSignupAttribution(
  signupStartedAt: string = new Date().toISOString()
): Promise<SignupAttributionPayload | null> {
  try {
    const attribution = await collectSignupAttribution()
    storageSet(PENDING_SIGNUP_KEY, { ...attribution, signup_started_at: signupStartedAt })
    return attribution
  } catch (error) {
    console.warn('[Attribution] Signup context could not be prepared:', error)
    return null
  }
}

export function clearPendingSignupAttribution(): void {
  storageRemove(PENDING_SIGNUP_KEY)
}

/** Attach only the actual newly created account returned by the signup provider. */
export function markPendingSignupAccount(userId?: string, createdAt?: string): boolean {
  const pending = storageGet<PendingSignup>(PENDING_SIGNUP_KEY)
  if (!pending || !userId || !createdAt || !pending.signup_started_at) return false
  const created = Date.parse(createdAt)
  const started = Date.parse(pending.signup_started_at)
  if (!Number.isFinite(created) || !Number.isFinite(started) || created < started - 10000)
    return false
  storageSet(PENDING_SIGNUP_KEY, {
    ...pending,
    account_id: userId,
    account_created_at: createdAt,
  })
  return true
}

export function isNewSignupAccount(user: SignupAccount): boolean {
  const pending = storageGet<PendingSignup>(PENDING_SIGNUP_KEY)
  const created = Date.parse(user.created_at)
  if (!Number.isFinite(created)) return false
  if (pending?.signup_started_at) {
    const started = Date.parse(pending.signup_started_at)
    if (Number.isFinite(started) && created >= started - 10000) {
      if (pending.account_id) {
        return pending.account_id === user.id && pending.account_created_at === user.created_at
      }
      // OAuth signups are proved by creation after the signup request, not button intent.
      return !user.last_sign_in_at || Date.parse(user.last_sign_in_at) >= created
    }
  }
  return (
    Date.now() - created >= 0 &&
    Date.now() - created < 120000 &&
    (!user.last_sign_in_at || Math.abs(Date.parse(user.last_sign_in_at) - created) < 1000)
  )
}

export async function retryPendingSignupAttribution(user: SignupAccount): Promise<boolean> {
  const pending = storageGet<PendingSignup>(PENDING_SIGNUP_KEY)
  // Ordinary authenticated visits never create or backfill historical attribution.
  if (!pending?.account_id || pending.account_id !== user.id || !isNewSignupAccount(user))
    return false
  return flushPendingSignupAttribution()
}

export async function flushPendingSignupAttribution(): Promise<boolean> {
  if (flushInFlight) return flushInFlight
  const pending = storageGet<PendingSignup>(PENDING_SIGNUP_KEY)
  if (!pending) return false
  flushInFlight = (async () => {
    try {
      // Consent may change between account request and confirmation. Re-enrich only
      // the original touch, and remove fields that no longer have consent.
      const firstTouch = captureFirstTouch()
      const attribution: SignupAttributionPayload = {
        ...pending,
        ...firstTouch,
      }
      for (const key of ['referrer', 'anonymous_id', ...UTM_KEYS] as const) {
        if (!hasConsent('analytics')) delete attribution[key]
      }
      const payload: PendingSignup = { ...attribution }
      delete payload.account_id
      delete payload.account_created_at
      const response = await api.funnel.saveSignupAttribution(payload)
      if (response.saved === false) return false
      storageRemove(PENDING_SIGNUP_KEY)
      return true
    } catch (error) {
      console.warn('[Attribution] Signup attribution was not saved:', error)
      return false
    } finally {
      flushInFlight = null
    }
  })()
  return flushInFlight
}

export async function trackFunnelEvent(
  event_name: FunnelEventName,
  properties: Partial<SignupAttributionPayload> & {
    selected_plan?: 'monthly' | 'yearly'
    billing_error_category?:
      | 'unavailable'
      | 'already_purchased'
      | 'network'
      | 'interrupted'
      | 'not_allowed'
      | 'configuration'
      | 'failed'
    billing_error_code?: string
  } = {}
): Promise<boolean> {
  // Public page events follow the existing analytics consent choice. Authenticated
  // product/billing steps are first-party operational funnel records.
  const publicEvent = ['landing_viewed', 'signup_viewed', 'signup_started'].includes(event_name)
  if (publicEvent && !hasConsent('analytics')) return false

  try {
    const context = await collectSignupAttribution()
    const response = await api.funnel.trackEvent({
      event_name,
      ...context,
      landing_path: context.initial_landing_path,
      ...properties,
    })
    return response.recorded === true
  } catch (error) {
    console.warn(`[Funnel] ${event_name} was not recorded:`, error)
    return false
  }
}

export function captureLandingViewedOnce() {
  if (
    !hasConsent('analytics') ||
    landingInFlight ||
    window.sessionStorage.getItem(LANDING_EVENT_KEY) === 'true'
  )
    return
  landingInFlight = true
  void trackFunnelEvent('landing_viewed')
    .then((recorded) => {
      if (recorded) window.sessionStorage.setItem(LANDING_EVENT_KEY, 'true')
    })
    .finally(() => {
      landingInFlight = false
    })
}
